import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, UserPlus, Users, Swords, CheckCheck, Ticket, XCircle, Banknote, Clock, Sun } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";

const typeIcons = {
  follow: UserPlus,
  match_join: Users,
  match_full: Swords,
  new_booking: Ticket,
  booking_cancelled: XCircle,
  refund_sent: Banknote,
  booking_reminder: Clock,
  daily_summary: Sun,
};

const typeColors = {
  follow: "text-teal",
  match_join: "text-coral",
  match_full: "text-golden",
  new_booking: "text-coral",
  booking_cancelled: "text-destructive",
  refund_sent: "text-green-500",
  booking_reminder: "text-golden",
  daily_summary: "text-golden",
};

export default function NotificationBell() {
  const { user } = useAuth();
  const { t, locale } = useLanguage();
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!user) return;
    const fetch = () => {
      api.get("/notifications").then(r => {
        setNotifs(r.data.notifications);
        setUnread(r.data.unread);
      }).catch(() => {});
    };
    fetch();
    const interval = setInterval(fetch, 15000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markRead = async () => {
    await api.put("/notifications/read");
    setUnread(0);
    setNotifs(n => n.map(x => ({ ...x, read: true })));
  };

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="icon"
        className="rounded-full w-9 h-9 relative"
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-coral text-white text-[9px] flex items-center justify-center font-bold pulse-coral">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-80 bg-card border border-border/50 rounded-2xl shadow-2xl overflow-hidden z-50"
            data-testid="notification-panel"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
              <h3 className="font-heading font-bold text-sm">{t("notif.title")}</h3>
              {unread > 0 && (
                <Button variant="ghost" size="sm" onClick={markRead} className="text-xs text-teal gap-1 h-7 rounded-full">
                  <CheckCheck size={12} /> {t("notif.read")}
                </Button>
              )}
            </div>
            <div className="max-h-72 overflow-y-auto">
              {notifs.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-8">{t("notif.none")}</p>
              )}
              {notifs.map(n => {
                const Icon = typeIcons[n.type] || Bell;
                const color = typeColors[n.type] || "text-muted-foreground";
                return (
                  <div
                    key={n._id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-border/20 transition-colors ${
                      !n.read ? "bg-coral/5" : ""
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${color} bg-current/10`}
                      style={{ backgroundColor: n.type === "follow" ? "rgba(0,109,119,0.1)" : n.type === "match_join" ? "rgba(31,95,74,0.1)" : "rgba(255,209,102,0.1)" }}
                    >
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs leading-relaxed">{n.message}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {new Date(n.created_at).toLocaleDateString(locale, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                    {!n.read && <div className="w-2 h-2 rounded-full bg-coral shrink-0 mt-1.5" />}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
